import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faSave } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import i18n from '../../i18n/config';

const PreferencesSettings: React.FC = () => {
  // @ts-ignore - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { t } = useTranslation('settings');
  const { business, updateBusinessProfile } = useAuth();
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const currentLang = lang || 'tr';

  const [selectedLang, setSelectedLang] = useState<'tr' | 'en'>('tr');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    // Load current language preference from business profile
    if (business?.preferences?.lang) {
      setSelectedLang(business.preferences.lang);
    } else {
      // Default to current URL language or 'tr'
      const urlLang = currentLang === 'tr' || currentLang === 'en' ? currentLang : 'tr';
      setSelectedLang(urlLang);
    }
  }, [business, currentLang]);

  const handleLangChange = (newLang: 'tr' | 'en') => {
    setSelectedLang(newLang);
    setMessage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    try {
      // Update business profile with new language preference
      await updateBusinessProfile({
        preferences: {
          ...business?.preferences,
          lang: selectedLang
        }
      });

      setMessage({ type: 'success', text: t('preferencesSettings.preferencesUpdated') });

      // Update i18n language
      i18n.changeLanguage(selectedLang);
      document.documentElement.lang = selectedLang;
      localStorage.setItem('i18nextLng', selectedLang);

      // Navigate to new language if different from current
      if (selectedLang !== currentLang) {
        const currentPath = window.location.pathname.replace(/^\/(tr|en)/, '');
        navigate(`/${selectedLang}${currentPath}`, { replace: true });
      }
    } catch (error: any) {
      console.error('Failed to update preferences:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || t('preferencesSettings.updateError') 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">{t('preferencesSettings.title')}</h2>
        <p className="text-white/70">{t('preferencesSettings.description')}</p>
      </div>

      {message && (
        <div
          className={`mb-6 p-4 rounded-xl ${
            message.type === 'success'
              ? 'bg-green-500/20 border border-green-500/30 text-green-400'
              : 'bg-red-500/20 border border-red-500/30 text-red-400'
          }`}
        >
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Language Selection */}
        <div>
          <label className="block text-sm font-medium text-white/80 mb-4">
            <div className="flex items-center space-x-2 mb-2">
              <FontAwesomeIcon icon={faGlobe} className="text-white/60" />
              <span>{t('preferencesSettings.language')}</span>
            </div>
            <p className="text-sm text-white/60 font-normal">
              {t('preferencesSettings.languageDesc')}
            </p>
          </label>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => handleLangChange('tr')}
              className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                selectedLang === 'tr'
                  ? 'bg-gradient-to-r from-purple-500/20 to-purple-700/20 border-purple-400/50 text-white'
                  : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-lg mb-1">{t('preferencesSettings.turkish')}</div>
                  <div className="text-sm opacity-80">Türkçe</div>
                </div>
                {selectedLang === 'tr' && (
                  <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                )}
              </div>
            </button>

            <button
              type="button"
              onClick={() => handleLangChange('en')}
              className={`p-4 rounded-xl border-2 transition-all duration-300 ${
                selectedLang === 'en'
                  ? 'bg-gradient-to-r from-purple-500/20 to-purple-700/20 border-purple-400/50 text-white'
                  : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-lg mb-1">{t('preferencesSettings.english')}</div>
                  <div className="text-sm opacity-80">English</div>
                </div>
                {selectedLang === 'en' && (
                  <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                )}
              </div>
            </button>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-start md:justify-end pt-6 border-t border-white/10">
          <button
            type="submit"
            disabled={isLoading}
            className="flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-purple-700 px-8 py-3 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-transparent transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>{t('preferencesSettings.saving')}</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="text-sm" />
                <span>{t('preferencesSettings.saveChanges')}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PreferencesSettings;

